import React, { useState, useEffect } from "react";
import { Dropdown, Space, Modal } from "antd";
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import Sidebar from "./Components/Sidebar";
import "./assets/css/style.css";
import Logo from "../assets/images/Logo.png";
import RoutesConfig from "./Components/RoutesConfig";
import CustomerCare from "./Components/CustomerCare";
import axios from "axios";
import { config } from "../config/config"; // Adjust the path as necessary
import CryptoJS from "crypto-js";

const ONE_HOUR = 60 * 60 * 1000; // 3 600 000 ms
const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;
const PIN_CHECK_DISABLED_KEY = "pinCheckDisabled";

function decryptData(ciphertext) {
  if (!ciphertext) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

function UserDashBoard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isPinModalVisible, setIsPinModalVisible] = useState(false);
  const [isPinCheckEnabled, setIsPinCheckEnabled] = useState(true);
  
  // Get user data using decryption
  const user = decryptData(localStorage.getItem("user"));
  const userId = user?._id || user?.id;

  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || "User",
    photo: user?.photo || null,
  });

  useEffect(() => {
    const handleProfileUpdate = () => {
      const updatedUser = decryptData(localStorage.getItem("user"));
      setProfileData({
        firstName: updatedUser?.firstName || "User",
        photo: updatedUser?.photo || null,
      });
    };
    window.addEventListener("profileUpdate", handleProfileUpdate);
    return () => window.removeEventListener("profileUpdate", handleProfileUpdate);
  }, []);


  useEffect(() => {
    // Disable PIN check if we're on the setpin page
    if (location.pathname === "/dashboard/setpin") {
      setIsPinCheckEnabled(false);
      localStorage.setItem(PIN_CHECK_DISABLED_KEY, "true");
    } else {
      const isDisabled = localStorage.getItem(PIN_CHECK_DISABLED_KEY);
      setIsPinCheckEnabled(!isDisabled);
    }
  }, [location.pathname]);

  useEffect(() => {
    const checkPin = async () => {
      // Don't check if disabled or no userId
      if (!isPinCheckEnabled || !userId) return;

      const dismissedUntil = localStorage.getItem("pinCheckDismissedUntil");
      if (dismissedUntil && new Date().getTime() < parseInt(dismissedUntil, 10)) {
        return;
      }

      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.walletBalance}${userId}`,
          {
            // Add retry configuration
            retry: 3,
            retryDelay: 2000,
            timeout: 5000,
          }
        );

        if (!response.data?.wallet?.pin) {
          setIsPinModalVisible(true);
        }
      } catch (error) {
        // Ignore 429 errors but log other errors
        if (error.response?.status !== 429) {
          
        }
        // Continue showing modal if we had previously determined PIN was needed
        if (isPinModalVisible) {
          setIsPinModalVisible(true);
        }
      }
    };

    // Only set up interval if checks are enabled
    if (isPinCheckEnabled) {
      checkPin();
      const interval = setInterval(checkPin, 120000);
      return () => clearInterval(interval);
    }
  }, [userId, isPinCheckEnabled]);


  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleSetupPin = () => {
    setIsPinModalVisible(false);
    setIsPinCheckEnabled(false);
    localStorage.setItem(PIN_CHECK_DISABLED_KEY, "true");
    navigate("/dashboard/setpin");
  };

  const handleRemindLater = () => {
    setIsPinModalVisible(false);
    const expireTime = new Date().getTime() + ONE_HOUR;
    localStorage.setItem("pinCheckDismissedUntil", expireTime.toString());
  };

  // Add cleanup when user sets PIN successfully
  useEffect(() => {
    const cleanupPinCheck = () => {
      localStorage.removeItem(PIN_CHECK_DISABLED_KEY);
      setIsPinCheckEnabled(true);
    };

    window.addEventListener("pinSetSuccess", cleanupPinCheck);
    return () => window.removeEventListener("pinSetSuccess", cleanupPinCheck);
  }, []);

  const items = [
    {
      key: "1",
      label: "Home",
      icon: <HomeOutlined />,
      onClick: () => navigate("/dashboard"),
    },
    {
      key: "2",
      label: "Profile",
      icon: <UserOutlined />,
      onClick: () => navigate("/dashboard/profile"),
    },
    {
      type: "divider",
    },
    {
      key: "3",
      label: "Logout",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
      danger: true,
    },
  ];



  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-100 text-gray-800 dark:text-gray-900">

      {/* ───── Rest of layout ───── */}
      <div className="flex justify-between items-center p-4  border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          {/* Mobile menu button */}
          <div className="md:hidden mr-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-700 dark:text-gray-200 focus:outline-none"
            >
              {sidebarOpen ? (
                <svg
                  className="w-6 h-6 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 text-sky-900"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
        {/* User dropdown */}
        <div className="flex items-center gap-4">
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <div className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 border-2 border-white shadow-sm flex items-center justify-center">
                {profileData.photo ? (
                  <img src={profileData.photo} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <UserOutlined className="text-gray-400 text-xl" />
                )}
              </div>
              <span className="text-gray-700 font-medium whitespace-nowrap block">
                Hello {profileData.firstName}
              </span>
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </Dropdown>
        </div>
      </div>

      <div className="flex flex-1 md:mt-[-40px]">
        {/* Sidebar */}
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />

        {/* Main Content */}
        <div
          className={`
            flex-1 min-w-0 overflow-hidden transition-all duration-300
            ${collapsed ? "md:ml-20" : "md:ml-64"}
          `}
        >
          <RoutesConfig />
        </div>
      </div>

      {/* Customer Care Component */}
      <CustomerCare />

      {/* Add PIN Setup Modal */}
      <Modal
        title="Transaction PIN Required"
        open={isPinModalVisible}
        onOk={handleSetupPin}
        onCancel={handleRemindLater}
        okText="Setup PIN Now"
        cancelText="Remind Me Later"
        okButtonProps={{
          style: { backgroundColor: "#f59e0b" },
        }}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-2">
            You haven't set up your transaction PIN yet. This is required for
            making transactions on our platform.
          </p>
          <p className="text-gray-600">
            Please set up your PIN to continue using our services securely.
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default UserDashBoard;
